import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

const PLATFORM_LABELS = {
  claude: 'Claude', chat_gpt: 'ChatGPT', gemini: 'Gemini', perplexity: 'Perplexity', google: 'Google AIO',
};
const DATAFORSEO_PLATFORMS = ['chat_gpt', 'gemini', 'perplexity'];

export default function GeoCitations() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [citations, setCitations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [queries, setQueries] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState({ claude: true, chat_gpt: false, gemini: false, perplexity: false });
  const [filterDays, setFilterDays] = useState(30);
  const [citedOnly, setCitedOnly] = useState(false);
  const [message, setMessage] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { loadData(); }, [siteId, filterDays, citedOnly]);

  async function loadData() {
    try {
      const [siteData, citData] = await Promise.all([
        api.getSite(siteId),
        api.getLlmVisibilityHistory(siteId, { days: filterDays, cited_only: citedOnly }),
      ]);
      setSite(siteData);
      setCitations(citData.citations || []);
      setSummary(citData.summary || null);
    } catch (err) { console.error('Failed to load citations:', err); }
    setLoading(false);
  }

  function togglePlatform(p) {
    setSelectedPlatforms(prev => ({ ...prev, [p]: !prev[p] }));
  }

  async function handleCheck(e) {
    e.preventDefault();
    const queryList = queries.split('\n').map(q => q.trim()).filter(q => q);
    if (!queryList.length) return;
    setChecking(true);
    setMessage(null);
    try {
      let citedCount = 0;
      let checksRun = 0;

      if (selectedPlatforms.claude) {
        const result = await api.checkCitations({ site_id: parseInt(siteId), queries: queryList, platform: 'claude' });
        citedCount += (result.results || []).filter(r => r.cited).length;
        checksRun += queryList.length;
      }

      const dfPlatforms = DATAFORSEO_PLATFORMS.filter(p => selectedPlatforms[p]);
      if (dfPlatforms.length) {
        const result = await api.checkLlmVisibility({ site_id: parseInt(siteId), queries: queryList, platforms: dfPlatforms });
        citedCount += result.cited_count || 0;
        checksRun += result.total_checks || 0;
      }

      if (!checksRun) {
        setMessage({ type: 'error', text: 'בחרו לפחות פלטפורמה אחת' });
      } else {
        setMessage({ type: 'success', text: 'נבדקו ' + checksRun + ' בדיקות - ' + citedCount + ' ציטוטים נמצאו' });
        setQueries('');
        await loadData();
      }
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
    setChecking(false);
  }

  async function generateSuggestions() {
    setSuggesting(true);
    setMessage(null);
    try {
      const result = await api.suggestCitationQueries(parseInt(siteId));
      const suggested = (result.queries || []).join('\n');
      setQueries(prev => {
        const existing = prev.trim();
        return existing ? existing + '\n' + suggested : suggested;
      });
    } catch (err) {
      setMessage({ type: 'error', text: 'שגיאה ביצירת הצעות: ' + err.message });
    }
    setSuggesting(false);
  }

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>טוען נתוני ציטוטים...</div>;
  if (!site) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>אתר לא נמצא</div>;

  const citationRate = summary?.citation_rate != null ? Math.round(summary.citation_rate) : null;
  const queryCount = queries.split('\n').map(q => q.trim()).filter(q => q).length;
  const selectedPlatformCount = Object.values(selectedPlatforms).filter(Boolean).length;
  const perPlatformRate = summary?.per_platform_rate || {};

  return (
    <div style={{ padding: '24px', maxWidth: '1000px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>AI Citation Monitor</h1>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>{site.name}</p>
      </div>

      {message && (
        <div style={{
          background: message.type === 'error' ? 'var(--red-glow)' : 'var(--green-glow)',
          border: '1px solid ' + (message.type === 'error' ? 'var(--red)' : 'var(--green)'),
          borderRadius: 'var(--radius-sm)', padding: '10px 16px', marginBottom: '16px',
          fontSize: '13px', color: message.type === 'error' ? 'var(--red)' : 'var(--green)',
        }}>{message.text}</div>
      )}

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '12px' }}>
        <div className="section-card" style={{ padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '26px', fontWeight: 800, fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{summary?.total_checks ?? 0}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>בדיקות</div>
        </div>
        <div className="section-card" style={{ padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '26px', fontWeight: 800, fontFamily: 'var(--mono)', color: 'var(--green)' }}>{summary?.total_cited ?? 0}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>צוטט</div>
        </div>
        <div className="section-card" style={{ padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '26px', fontWeight: 800, fontFamily: 'var(--mono)',
            color: citationRate != null ? (citationRate >= 50 ? 'var(--green)' : citationRate >= 20 ? 'var(--yellow)' : 'var(--red)') : 'var(--text-muted)'
          }}>{citationRate != null ? citationRate + '%' : '-'}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>שיעור ציטוט</div>
        </div>
      </div>

      {/* Per-platform breakdown */}
      {Object.keys(perPlatformRate).length > 1 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {Object.entries(perPlatformRate).map(([p, rate]) => (
            <div key={p} className="section-card" style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{PLATFORM_LABELS[p] || p}</span>
              <span style={{ fontSize: '13px', fontWeight: 700, fontFamily: 'var(--mono)',
                color: rate >= 50 ? 'var(--green)' : rate >= 20 ? 'var(--yellow)' : 'var(--red)' }}>{rate}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Check Form */}
      <div className="section-card" style={{ padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>בדיקת ציטוטים חדשה</h3>
        </div>

        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '14px' }}>
          {Object.keys(PLATFORM_LABELS).filter(p => p !== 'google').map(p => (
            <label key={p} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!selectedPlatforms[p]} onChange={() => togglePlatform(p)}
                style={{ accentColor: 'var(--accent)' }} />
              {PLATFORM_LABELS[p]}
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>
              שאילתות (שאלה אחת בכל שורה, עד 20)
            </label>
            <textarea value={queries} onChange={e => setQueries(e.target.value)}
              placeholder={"מי הגנן הכי טוב בתל אביב?\nאיזה שירותי גינון מומלצים?\nמה כדאי לדעת לפני שבוחרים גנן?"} rows={5} className="geo-input"
              style={{ resize: 'vertical', fontFamily: 'var(--sans)' }} />
            {queryCount > 0 && selectedPlatformCount > 0 && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                {queryCount} שאילתות × {selectedPlatformCount} פלטפורמות = {queryCount * selectedPlatformCount} בדיקות
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingTop: '20px' }}>
            <button className="btn btn-primary" onClick={handleCheck} disabled={checking || !queries.trim() || !selectedPlatformCount}
              style={{ whiteSpace: 'nowrap' }}>
              {checking ? 'בודק...' : 'בדוק ציטוטים'}
            </button>
            <button className="btn btn-sm" onClick={generateSuggestions} disabled={suggesting}
              style={{ whiteSpace: 'nowrap' }}>
              {suggesting ? 'יוצר הצעות...' : '✨ הצע שאילתות חכמות'}
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px' }}>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>תקופה:</span>
        {[7, 30, 90].map(d => (
          <button key={d} onClick={() => setFilterDays(d)}
            className={'filter-chip' + (filterDays === d ? ' active' : '')}>{d} ימים</button>
        ))}
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer', marginRight: '8px' }}>
          <input type="checkbox" checked={citedOnly} onChange={e => setCitedOnly(e.target.checked)}
            style={{ accentColor: 'var(--accent)' }} />
          רק ציטוטים
        </label>
      </div>

      {/* Table */}
      <div className="section-card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              {['שאילתה','פלטפורמה','צוטט','מתחרים שצוטטו','סנטימנט','תאריך'].map((h,i) => (
                <th key={i} style={{ padding: '12px 14px', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)',
                  textAlign: i === 0 ? 'right' : 'center', letterSpacing: '0.3px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {citations.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)', fontSize: '13px' }}>
                אין נתוני ציטוטים עדיין. הריצו בדיקה ראשונה למעלה.
              </td></tr>
            ) : citations.map((c, idx) => {
              const hasSources = (c.sources || []).length > 0;
              const isExpanded = expandedId === c.id;
              return (
              <React.Fragment key={c.id || idx}>
                <tr style={{ borderBottom: isExpanded ? 'none' : '1px solid var(--border)' }}>
                  <td style={{ padding: '12px 14px', fontSize: '13px', maxWidth: '300px' }}>
                    <div style={{ fontWeight: 500 }}>{c.query}</div>
                    {c.citation_text && (
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', lineHeight: 1.4 }}>
                        {c.citation_text.length > 150 ? c.citation_text.slice(0, 150) + '...' : c.citation_text}
                      </div>
                    )}
                    {hasSources && (
                      <button onClick={() => setExpandedId(isExpanded ? null : c.id)}
                        style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: '11px', cursor: 'pointer', padding: '4px 0 0', textAlign: 'right' }}>
                        {isExpanded ? 'הסתר מקורות ▲' : 'מקורות (' + c.sources.length + ') ▼'}
                      </button>
                    )}
                  </td>
                  <td style={{ padding: '12px 10px', fontSize: '11px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                    {PLATFORM_LABELS[c.platform] || c.platform}
                  </td>
                  <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-flex', width: '26px', height: '26px', alignItems: 'center', justifyContent: 'center',
                      borderRadius: '50%', fontSize: '13px', fontWeight: 700,
                      background: c.cited ? 'var(--green-glow)' : 'var(--red-glow)',
                      color: c.cited ? 'var(--green)' : 'var(--red)',
                    }}>{c.cited ? '✓' : '✗'}</span>
                  </td>
                  <td style={{ padding: '12px 10px', fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                    {(c.competitor_cited || []).length > 0
                      ? (c.competitor_cited || []).map(x => typeof x === 'string' ? x : x.name || x.domain || JSON.stringify(x)).join(', ')
                      : <span style={{ color: 'var(--text-muted)' }}>-</span>}
                  </td>
                  <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                    <SentimentBadge sentiment={c.sentiment} />
                  </td>
                  <td style={{ padding: '12px 10px', fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--mono)', textAlign: 'center' }}>
                    {c.checked_at ? new Date(c.checked_at).toLocaleDateString('he-IL') : '-'}
                  </td>
                </tr>
                {isExpanded && hasSources && (
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <td colSpan={6} style={{ padding: '4px 14px 14px', background: 'var(--bg)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {c.sources.map((s, si) => (
                          <div key={si} style={{ fontSize: '11px', display: 'flex', gap: '8px', alignItems: 'baseline' }}>
                            <span style={{ color: 'var(--text-muted)' }}>#{si + 1}</span>
                            {s.url ? (
                              <a href={s.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', direction: 'ltr', textAlign: 'left' }}>
                                {s.title || s.domain || s.url}
                              </a>
                            ) : <span>{s.title || s.domain || JSON.stringify(s)}</span>}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SentimentBadge({ sentiment }) {
  if (!sentiment) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
  const m = { positive: { c: 'var(--green)', t: 'חיובי' }, neutral: { c: 'var(--text-secondary)', t: 'ניטרלי' }, negative: { c: 'var(--red)', t: 'שלילי' } };
  const s = m[sentiment] || m.neutral;
  return <span style={{ fontSize: '11px', color: s.c, fontWeight: 500 }}>{s.t}</span>;
}
